import type { AddressUsage } from '~/api/DeliveryAddress';
import { DeliveryMethods } from '~/api/ETA';
import type { DeliveryAddress } from '~/stores/UserStore';

import { resolveAddressUsage } from './addressUsage';

type AddressPoint = Partial<Pick<DeliveryAddress, 'addressId' | 'coordinates'>>;

/**
 * True when an address change is only the active address getting the id of
 * its own save: it had no id (a guest address, or one the last order left
 * unlinked) and now has one at the same point. That is the same place, not a
 * new choice of address, so the delivery method picked for it must stay.
 */
export const isSameAddressSaved = (
  prev: AddressPoint | undefined,
  next: AddressPoint | undefined,
): boolean =>
  !prev?.addressId &&
  !!next?.addressId &&
  !!prev?.coordinates &&
  !!next.coordinates &&
  prev.coordinates.lat === next.coordinates.lat &&
  prev.coordinates.lng === next.coordinates.lng;

/**
 * Maps a saved address usage tag to the delivery method it represents.
 * Pickup → CLICK_AND_COLLECT, delivery → COURIER_DELIVERY. An untagged address
 * resolves through the tenant default, which is pickup where every address is
 * a pickup point (see {@link resolveAddressUsage}).
 */
export const deliveryMethodForUsage = (
  usage?: AddressUsage,
): DeliveryMethods =>
  resolveAddressUsage(usage) === 'pickup'
    ? DeliveryMethods.CLICK_AND_COLLECT
    : DeliveryMethods.COURIER_DELIVERY;

/**
 * Inverse of {@link deliveryMethodForUsage}: the saved-address type that
 * fulfils a given delivery method.
 */
export const usageForDeliveryMethod = (
  method: DeliveryMethods,
): AddressUsage =>
  method === DeliveryMethods.CLICK_AND_COLLECT ? 'pickup' : 'delivery';
